if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        // Druvient uses the cart drawer as the primary cart UI.
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.form || !this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          this.cart.setActiveElement?.(document.activeElement);

          // Ask Shopify to render the fresh cart drawer in the SAME cart/add
          // response. This follows the Ajax Cart API section-rendering flow,
          // removes the normal-path second request, and is more reliable on
          // Mobile Safari when the cart changes from empty -> first item.
          const sectionIds = typeof this.cart.getSectionsToRender === 'function'
            ? this.cart.getSectionsToRender().map((section) => section?.id).filter(Boolean)
            : ['cart-drawer', 'cart-icon-bubble'];

          if (!sectionIds.includes('cart-drawer')) sectionIds.unshift('cart-drawer');
          if (!sectionIds.includes('cart-icon-bubble')) sectionIds.push('cart-icon-bubble');

          formData.set('sections', [...new Set(sectionIds)].join(','));
          formData.set('sections_url', window.location.pathname || '/');
        }

        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        try {
          const addRequest = async () => {
            const response = await fetch(`${routes.cart_add_url}`, {
              ...config,
              credentials: 'same-origin',
              cache: 'no-store',
            });

            const responseText = await response.text();
            let data = null;

            try {
              data = responseText ? JSON.parse(responseText) : null;
            } catch (_) {
              data = null;
            }

            return { response, data };
          };

          const { response, data } = window.RDDurvientCartQueue?.run
            ? await window.RDDurvientCartQueue.run(addRequest)
            : await addRequest();

          if (!response.ok || data.status) {
            const message = data.errors || data.description || data.message || 'Unable to add product';

            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: data.errors || data.description,
              message: data.message,
            });

            this.handleErrorMessage(data.description || data.message || message);
            this.dispatchCartErrorEvent(data.description || data.message || String(message), 'INVALID');
            linesUpdateDeferred?.reject(new Error(String(message)));

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage && this.submitButtonText) {
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            this.error = true;
            return;
          }

          this.error = false;
          this.resolveCartLinesUpdate(linesUpdateDeferred);

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          const paintCart = async () => {
            try {
              // Preferred path: use the cart-drawer HTML Shopify returned with
              // this exact add request. No extra request and no stale empty-cart
              // drawer on iPhone / Mobile Safari.
              if (
                data?.sections?.['cart-drawer'] &&
                typeof this.cart.renderContents === 'function'
              ) {
                const applied = this.cart.renderContents(data, true);
                if (applied !== false) return true;
              }

              // Defensive fallback if a section failed to render. Shopify can
              // return null for a requested section even when the cart mutation
              // itself succeeded.
              if (typeof this.cart.refreshFromCart === 'function') {
                return await this.cart.refreshFromCart({ open: true });
              }

              this.cart.open?.();
              return true;
            } catch (error) {
              // Shopify already accepted the add. Never turn a drawer repaint
              // issue into a false add-to-cart error.
              console.warn('[RD Product Form] Product added; drawer paint failed', error);
              this.cart.open?.();
              return false;
            }
          };

          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => window.setTimeout(() => {
                paintCart().catch(() => {});
              }, 0),
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            // Paint the new line item and open the drawer immediately, before
            // slower pub/sub subscribers perform their own background refresh.
            await paintCart();
          }

          // Notify Dawn after the drawer is already painted/open. CartItems,
          // quantity rules, analytics and other subscribers still receive the
          // standard event, but they cannot delay the visible PDP response.
          try {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: data,
            });
          } catch (error) {
            console.warn('[RD Product Form] cartUpdate subscriber failed', error);
          }
        } catch (error) {
          console.error(error);
          this.dispatchCartErrorEvent(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(error);
        } finally {
          this.submitButton.classList.remove('loading');

          if (this.cart?.classList?.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }

          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');

          if (typeof CartPerformance !== 'undefined') {
            try {
              CartPerformance.measureFromEvent('add:user-action', evt);
            } catch (_) {}
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent || !variantId) return null;

        const deferred = CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );

        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;

        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              deferred.reject(new Error('Missing currency in cart response'));
              return;
            }

            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}